/**
 * Cookie utility functions for token management
 */

import { getEnvVar } from '../api/ApiUrls';
import { isWebBuild } from './environment';

/**
 * Extracts the parent domain from the current URL to share cookies across subdomains
 * @returns Parent domain with leading dot (e.g., '.ideoz.ai') or empty string for localhost
 */
const getParentDomain = (): string => {
    if (typeof window === 'undefined') return '';

    try {
        const hostname = window.location.hostname;

        // For localhost or IP addresses, don't set domain
        if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
            return '';
        }

        // Split hostname by dots
        const parts = hostname.split('.');

        // If it's already a top-level domain (e.g., ideoz.ai), use it with leading dot
        if (parts.length <= 2) {
            return `.${hostname}`;
        }

        // For subdomains (e.g., admin.ideoz.ai), extract parent domain
        const parentDomain = parts.slice(-2).join('.');
        return `.${parentDomain}`;
    } catch (e) {
        // Silently fail - window.location not available in plugin environment
        return '';
    }
};

/**
 * Sets a cookie with the specified name, value, and expiration days
 * @param name - Cookie name
 * @param value - Cookie value
 * @param days - Number of days until expiration (default: 30)
 */
export const setCookie = (name: string, value: string, days: number = 30): void => {
    if (typeof window === 'undefined') return;

    try {
        const expires = new Date();
        expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
        const isSecure = window.location.protocol === 'https:';
        const domain = getParentDomain();
        const domainAttribute = domain ? `;domain=${domain}` : '';
        document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax${isSecure ? ';Secure' : ''}${domainAttribute}`;
    } catch (e) {
        // Silently fail - cookies not available in plugin environment
    }
};

/**
 * Gets a cookie value by name
 * @param name - Cookie name
 * @returns Cookie value or null if not found
 */
export const getCookie = (name: string): string | null => {
    if (typeof window === 'undefined') return null;

    try {
        const nameEQ = name + "=";
        const ca = document.cookie.split(';');
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) === ' ') c = c.substring(1, c.length);
            if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
        }
    } catch (e) {
        // Silently fail - cookies not available in plugin environment
    }
    return null;
};

/**
 * Removes a cookie by name
 * @param name - Cookie name
 */
export const removeCookie = (name: string): void => {
    if (typeof window === 'undefined') return;

    try {
        const domain = getParentDomain();
        const domainAttribute = domain ? `;domain=${domain}` : '';
        // Remove with current domain attribute
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/${domainAttribute};`;
        // Also try removing without domain to handle cross-subdomain cookie mismatch
        if (domainAttribute) {
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        }
    } catch (e) {
        // Silently fail - cookies not available in plugin environment
    }
};

/**
 * Token-specific cookie utilities
 * Cookie prefix is configurable via VITE_COOKIE_PREFIX env var (defaults to 'i')
 */
const COOKIE_PREFIX = getEnvVar('VITE_COOKIE_PREFIX') || 'i';
const TOKEN_COOKIE_KEY = `${COOKIE_PREFIX || 'i'}_a_t`;
const REFRESH_TOKEN_COOKIE_KEY = `${COOKIE_PREFIX || 'i'}_r_t`;
const TOKEN_STORAGE_KEY = 'ideoz_access_token';
const REFRESH_TOKEN_STORAGE_KEY = 'ideoz_refresh_token';

/**
 * Saves access token to cookie (web) or window/localStorage (plugin)
 * For web: Uses secure, domain-shared cookies as primary storage
 * For plugin: Uses window and Figma storage
 * @param token - Access token to save
 */
export const saveAccessToken = (token: string): void => {
    // Save to window (plugin environment - always works)
    if (typeof window !== 'undefined') {
        (window as any).appAccessToken = token;
    }
    
    // Notify plugin controller to save in Figma storage (plugin only)
    if (!isWebBuild() && typeof parent !== 'undefined') {
        try {
            parent.postMessage(
                { pluginMessage: { type: 'SAVE_ACCESS_TOKEN', code: token } },
                '*'
            );
            console.log('📤 Notified plugin to save access token');
        } catch (e) {
            console.debug('Plugin messaging not available:', e);
        }
    }
    
    // Save to cookie (web environment - primary storage for web)
    if (isWebBuild()) {
        try {
            setCookie(TOKEN_COOKIE_KEY, token, 30);
        } catch (e) {
            console.debug('Cookie storage not available:', e);
        }
        // Verify cookie was actually written (Bug 7: detect silent write failures)
        try {
            const verified = getCookie(TOKEN_COOKIE_KEY);
            if (!verified) {
                console.warn('⚠️ Access token cookie write failed (possible quota/3rd-party blocking). Token only in memory.');
            }
        } catch (e) {
            // Verification failed, token is still in window.appAccessToken
        }
    }
    
    // Save to localStorage as fallback for non-web environments only
    if (!isWebBuild()) {
        try {
            if (typeof window !== 'undefined' && window.localStorage) {
                localStorage.setItem(TOKEN_STORAGE_KEY, token);
            }
        } catch (e) {
            console.debug('localStorage not available:', e);
        }
    }
};

/**
 * Saves refresh token to cookie (web) or window/localStorage (plugin)
 * For web: Uses secure, domain-shared cookies as primary storage
 * For plugin: Uses window and Figma storage
 * @param token - Refresh token to save
 */
export const saveRefreshToken = (token: string): void => {
    // Save to window.appRefreshToken (plugin environment - always works)
    if (typeof window !== 'undefined') {
        (window as any).appRefreshToken = token;
    }

    // Notify plugin controller to save in Figma storage (plugin only)
    if (!isWebBuild() && typeof parent !== 'undefined') {
        try {
            parent.postMessage(
                { pluginMessage: { type: 'SAVE_REFRESH_TOKEN', code: token } },
                '*'
            );
            console.log('📤 Notified plugin to save refresh token');
        } catch (e) {
            console.debug('Plugin messaging not available:', e);
        }
    }

    // Save to cookie (web environment - primary storage for web)
    // Use 365 days to match server-side refresh token lifetime (1 year)
    if (isWebBuild()) {
        try {
            setCookie(REFRESH_TOKEN_COOKIE_KEY, token, 365);
        } catch (e) {
            console.debug('Cookie storage not available:', e);
        }
    }

    // Save to localStorage for non-web environments only
    if (!isWebBuild()) {
        try {
            if (typeof window !== 'undefined' && window.localStorage) {
                localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, token);
            }
        } catch (e) {
            console.debug('localStorage not available:', e);
        }
    }
};

/**
 * Gets access token from cookie (web) or localStorage (plugin)
 * For web: Reads from secure, domain-shared cookies
 * For plugin: Reads from localStorage
 * @returns Access token or null if not found
 */
export const getAccessToken = (): string | null => {
    // Web build: Try cookie only
    if (isWebBuild()) {
        try {
            const cookieToken = getCookie(TOKEN_COOKIE_KEY);
            if (cookieToken) {
                return cookieToken;
            }
        } catch (e) {
            console.debug('Cookie read not available:', e);
        }
        return null;
    }

    // Plugin build: Try localStorage only
    try {
        if (typeof window !== 'undefined' && window.localStorage) {
            return localStorage.getItem(TOKEN_STORAGE_KEY);
        }
    } catch (e) {
        console.debug('localStorage not available:', e);
    }

    return null;
};

/**
 * Gets refresh token from cookie (web) or window/localStorage (plugin)
 * For web: Reads from secure, domain-shared cookies
 * For plugin: Reads from window or localStorage
 * @returns Refresh token or null if not found
 */
export const getRefreshToken = (): string | null => {
    // Web build: Try cookie only
    if (isWebBuild()) {
        try {
            const cookieToken = getCookie(REFRESH_TOKEN_COOKIE_KEY);
            if (cookieToken) {
                return cookieToken;
            }
        } catch (e) {
            console.debug('Cookie read not available:', e);
        }
        return null;
    }

    // Plugin build: Check window.appRefreshToken first
    if (typeof window !== 'undefined' && (window as any).appRefreshToken) {
        return (window as any).appRefreshToken;
    }

    // Plugin build: Fallback to localStorage
    try {
        if (typeof window !== 'undefined' && window.localStorage) {
            return localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
        }
    } catch (e) {
        console.debug('localStorage not available:', e);
    }
    return null;
};

/**
 * Removes access token and refresh token from all storage (full auth clear)
 * For web: Removes from cookies
 * For plugin: Removes from window and localStorage
 */
export const removeAccessToken = (): void => {
    // Remove from cookies (web environment)
    if (isWebBuild()) {
        try {
            removeCookie(TOKEN_COOKIE_KEY);
            removeCookie(REFRESH_TOKEN_COOKIE_KEY);
        } catch (e) {
            console.debug('Cookie removal not available:', e);
        }
    }
    
    // Remove from window (plugin environment)
    if (typeof window !== 'undefined') {
        delete (window as any).appAccessToken;
        delete (window as any).appRefreshToken;
    }
    
    // Remove from localStorage (plugin environment)
    if (!isWebBuild()) {
        try {
            if (typeof window !== 'undefined' && window.localStorage) {
                localStorage.removeItem(TOKEN_STORAGE_KEY);
                localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
            }
        } catch (e) {
            console.debug('localStorage removal not available:', e);
        }
    }
};

/**
 * Removes only the refresh token from cookies/window/localStorage (access token unchanged)
 * Gracefully handles errors in restricted environments
 */
export const removeRefreshToken = (): void => {
    // Remove from cookie (web environment)
    if (isWebBuild()) {
        try {
            removeCookie(REFRESH_TOKEN_COOKIE_KEY);
        } catch (e) {
            console.debug('Cookie removal not available:', e);
        }
    }
    
    // Remove from window (plugin environment)
    if (typeof window !== 'undefined') {
        delete (window as any).appRefreshToken;
    }
    
    // Remove from localStorage (plugin environment)
    if (!isWebBuild()) {
        try {
            if (typeof window !== 'undefined' && window.localStorage) {
                localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
            }
        } catch (e) {
            console.debug('localStorage removal not available:', e);
        }
    }
};

